import React from "react";
import ReactDOM from "react-dom";
import cn from "./helpers/cn";

type Placement =
  | "top" | "top-start" | "top-end"
  | "bottom" | "bottom-start" | "bottom-end"
  | "left" | "left-start" | "left-end"
  | "right" | "right-start" | "right-end";

export interface TooltipProps {
  title: React.ReactNode;
  children: React.ReactElement;
  placement?: Placement;
  arrow?: boolean;
  enterDelay?: number;
  enterNextDelay?: number;
  enterTouchDelay?: number;
  leaveDelay?: number;
  leaveTouchDelay?: number;
  open?: boolean;
  onOpen?: (event: React.SyntheticEvent) => void;
  onClose?: (event: React.SyntheticEvent) => void;
  disableHoverListener?: boolean;
  disableFocusListener?: boolean;
  disableTouchListener?: boolean;
  interactive?: boolean;
  PopperProps?: React.HTMLAttributes<HTMLDivElement>;
  className?: string;
  style?: React.CSSProperties;
  id?: string;
}

// Timestamp do último Tooltip que escondeu — usado por enterNextDelay (igual ao MUI).
let lastHideTime = 0;

// Distância entre o tooltip e o elemento âncora (px). MUI v4 usa ~14px com arrow.
const OFFSET = 8;
const ARROW_OFFSET = 14;

interface Position {
  top: number;
  left: number;
  // Posição efetiva (espelhando o MUI/Popper quando há flip por colisão).
  resolvedPlacement: Placement;
}

function computePosition(
  anchor: DOMRect,
  size: { width: number; height: number; },
  placement: Placement,
  offset: number
): Position {
  const [side, align = "center"] = placement.split("-") as [string, string?];

  let top = 0;
  let left = 0;

  // Posição base no eixo perpendicular ao side.
  if (side === "top") top = anchor.top - size.height - offset;
  else if (side === "bottom") top = anchor.bottom + offset;
  else if (side === "left") left = anchor.left - size.width - offset;
  else if (side === "right") left = anchor.right + offset;

  // Posição no eixo paralelo ao side (alinhamento).
  if (side === "top" || side === "bottom") {
    if (align === "start") left = anchor.left;
    else if (align === "end") left = anchor.right - size.width;
    else left = anchor.left + (anchor.width - size.width) / 2;
  } else {
    if (align === "start") top = anchor.top;
    else if (align === "end") top = anchor.bottom - size.height;
    else top = anchor.top + (anchor.height - size.height) / 2;
  }

  return { top, left, resolvedPlacement: placement };
}

// Classes para posicionar a seta dentro do tooltip, conforme o side resultante.
const arrowClass: Record<Placement, string> = {
  "top": "top-full left-1/2 -translate-x-1/2 border-t-slate-700 border-x-transparent border-b-0",
  "top-start": "top-full left-3 border-t-slate-700 border-x-transparent border-b-0",
  "top-end": "top-full right-3 border-t-slate-700 border-x-transparent border-b-0",
  "bottom": "bottom-full left-1/2 -translate-x-1/2 border-b-slate-700 border-x-transparent border-t-0",
  "bottom-start": "bottom-full left-3 border-b-slate-700 border-x-transparent border-t-0",
  "bottom-end": "bottom-full right-3 border-b-slate-700 border-x-transparent border-t-0",
  "left": "left-full top-1/2 -translate-y-1/2 border-l-slate-700 border-y-transparent border-r-0",
  "left-start": "left-full top-2 border-l-slate-700 border-y-transparent border-r-0",
  "left-end": "left-full bottom-2 border-l-slate-700 border-y-transparent border-r-0",
  "right": "right-full top-1/2 -translate-y-1/2 border-r-slate-700 border-y-transparent border-l-0",
  "right-start": "right-full top-2 border-r-slate-700 border-y-transparent border-l-0",
  "right-end": "right-full bottom-2 border-r-slate-700 border-y-transparent border-l-0",
};

const Tooltip = React.forwardRef<HTMLElement, TooltipProps>((props, ref) => {
  const {
    title,
    children,
    placement = "bottom",
    arrow = false,
    enterDelay = 100,
    enterNextDelay = 0,
    enterTouchDelay = 700,
    leaveDelay = 0,
    leaveTouchDelay = 1500,
    open: openProp,
    onOpen,
    onClose,
    disableHoverListener = false,
    disableFocusListener = false,
    disableTouchListener = false,
    interactive = false,
    PopperProps,
    className,
    style,
    id,
  } = props;

  const [openState, setOpenState] = React.useState(false);
  const isControlled = openProp !== undefined;
  const open = isControlled ? !!openProp : openState;

  const enterTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const leaveTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const touchTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const anchorRef = React.useRef<HTMLElement | null>(null);
  const tooltipRef = React.useRef<HTMLDivElement | null>(null);
  const [position, setPosition] = React.useState<Position>({ top: 0, left: 0, resolvedPlacement: placement });

  const clearTimers = React.useCallback(() => {
    if (enterTimer.current) clearTimeout(enterTimer.current);
    if (leaveTimer.current) clearTimeout(leaveTimer.current);
    if (touchTimer.current) clearTimeout(touchTimer.current);
  }, []);

  React.useEffect(() => clearTimers, [clearTimers]);

  // Recalcula posição quando abrir / mudar título / mudar placement.
  const updatePosition = React.useCallback(() => {
    const anchor = anchorRef.current;
    const tip = tooltipRef.current;

    if (!anchor || !tip) return;
    const aRect = anchor.getBoundingClientRect();
    const size = { width: tip.offsetWidth, height: tip.offsetHeight };

    setPosition(computePosition(aRect, size, placement, arrow ? ARROW_OFFSET : OFFSET));
  }, [placement, arrow]);

  React.useLayoutEffect(() => {
    if (open) updatePosition();
  }, [open, title, updatePosition]);

  React.useEffect(() => {
    if (!open) return;
    const handler = () => updatePosition();

    window.addEventListener("resize", handler);
    window.addEventListener("scroll", handler, true);

    return () => {
      window.removeEventListener("resize", handler);
      window.removeEventListener("scroll", handler, true);
    };
  }, [open, updatePosition]);

  const show = (event: React.SyntheticEvent, delay: number) => {
    if (leaveTimer.current) clearTimeout(leaveTimer.current);

    // enterNextDelay: se outro tooltip acabou de fechar dentro dessa janela,
    // mostra imediatamente (mesmo comportamento do MUI v4).
    const effectiveDelay = (enterNextDelay > 0 && Date.now() - lastHideTime < enterNextDelay) ? 0 : delay;

    enterTimer.current = setTimeout(() => {
      if (!isControlled) setOpenState(true);
      onOpen?.(event);
    }, effectiveDelay);
  };

  const hide = (event: React.SyntheticEvent, delay = leaveDelay) => {
    if (enterTimer.current) clearTimeout(enterTimer.current);
    leaveTimer.current = setTimeout(() => {
      if (!isControlled) setOpenState(false);
      lastHideTime = Date.now();
      onClose?.(event);
    }, delay);
  };

  // Lida com mouse na ÂNCORA.
  const handleMouseEnter = (event: React.MouseEvent) => {
    if (disableHoverListener) return;
    show(event, enterDelay);
  };

  const handleMouseLeave = (event: React.MouseEvent) => {
    if (disableHoverListener) return;
    hide(event);
  };

  const handleFocus = (event: React.FocusEvent) => {
    if (disableFocusListener) return;
    show(event, enterDelay);
  };

  const handleBlur = (event: React.FocusEvent) => {
    if (disableFocusListener) return;
    hide(event);
  };

  const handleTouchStart = (event: React.TouchEvent) => {
    if (disableTouchListener) return;
    if (touchTimer.current) clearTimeout(touchTimer.current);
    touchTimer.current = setTimeout(() => show(event, 0), enterTouchDelay);
  };

  const handleTouchEnd = (event: React.TouchEvent) => {
    if (disableTouchListener) return;
    if (touchTimer.current) clearTimeout(touchTimer.current);
    hide(event, leaveTouchDelay);
  };

  // Mouse no TOOLTIP (modo interactive): cancela hide pendente.
  const handleTooltipMouseEnter = () => {
    if (!interactive) return;
    if (leaveTimer.current) clearTimeout(leaveTimer.current);
  };

  const handleTooltipMouseLeave = (event: React.MouseEvent) => {
    if (!interactive) return;
    hide(event);
  };

  // Quando title é vazio/null/false, renderiza só o filho (igual MUI).
  const titleIsEmpty = title === null || title === undefined || title === "" || title === false;

  // Combina ref interno com o ref do filho (se existir).
  const setAnchorRef = (node: HTMLElement | null) => {
    anchorRef.current = node;
    if (typeof ref === "function") ref(node);
    else if (ref) (ref as React.MutableRefObject<HTMLElement | null>).current = node;
    // Propaga para o ref original do filho.
    const childRef = (children as React.ReactElement & { ref?: React.Ref<HTMLElement>; }).ref;

    if (typeof childRef === "function") childRef(node);
    else if (childRef && typeof childRef === "object") {
      (childRef as React.MutableRefObject<HTMLElement | null>).current = node;
    }
  };

  // Clona o filho injetando handlers + ref. (Componentes funcionais sem forwardRef
  // não receberão ref — comportamento e warning idênticos aos do MUI.)
  const childWithEvents = React.cloneElement(children, {
    ref: setAnchorRef,
    onMouseEnter: (e: React.MouseEvent) => {
      handleMouseEnter(e);
      (children.props as { onMouseEnter?: (e: React.MouseEvent) => void; }).onMouseEnter?.(e);
    },
    onMouseLeave: (e: React.MouseEvent) => {
      handleMouseLeave(e);
      (children.props as { onMouseLeave?: (e: React.MouseEvent) => void; }).onMouseLeave?.(e);
    },
    onFocus: (e: React.FocusEvent) => {
      handleFocus(e);
      (children.props as { onFocus?: (e: React.FocusEvent) => void; }).onFocus?.(e);
    },
    onBlur: (e: React.FocusEvent) => {
      handleBlur(e);
      (children.props as { onBlur?: (e: React.FocusEvent) => void; }).onBlur?.(e);
    },
    onTouchStart: (e: React.TouchEvent) => {
      handleTouchStart(e);
      (children.props as { onTouchStart?: (e: React.TouchEvent) => void; }).onTouchStart?.(e);
    },
    onTouchEnd: (e: React.TouchEvent) => {
      handleTouchEnd(e);
      (children.props as { onTouchEnd?: (e: React.TouchEvent) => void; }).onTouchEnd?.(e);
    },
    "aria-describedby": open ? id : (children.props as { "aria-describedby"?: string; })["aria-describedby"],
  } as Partial<React.HTMLAttributes<HTMLElement>>);

  if (titleIsEmpty) return childWithEvents;

  // Tooltip rendered via portal para evitar clipping de overflow parents.
  const portal = typeof document !== "undefined"
    ? ReactDOM.createPortal(
      <div
        ref={tooltipRef}
        role="tooltip"
        id={id}
        aria-hidden={!open}
        onMouseEnter={handleTooltipMouseEnter}
        onMouseLeave={handleTooltipMouseLeave}
        {...PopperProps}
        style={{
          position: "fixed",
          top: position.top,
          left: position.left,
          // Sem `pointer-events-none` em interactive para permitir hover no tooltip.
          pointerEvents: interactive ? "auto" : "none",
          ...PopperProps?.style,
          ...style,
        }}
        className={cn(
          "z-[1500] transition-opacity duration-150",
          "max-w-xs px-2 py-1 rounded text-xs font-medium leading-tight",
          "bg-slate-700 text-white whitespace-normal break-words shadow",
          open ? "opacity-100" : "opacity-0",
          className,
          PopperProps?.className
        )}
      >
        {title}

        {arrow && (
          <span
            aria-hidden="true"
            className={cn(
              "absolute w-0 h-0 border-[5px] border-solid",
              arrowClass[position.resolvedPlacement]
            )}
          />
        )}
      </div>,
      document.body
    )
    : null;

  return <>{childWithEvents}{portal}</>;
});

Tooltip.displayName = "Tooltip";

export default Tooltip;
